<template>
    <div class="federation-users-tab md-layout">
        <div class="md-layout-item md-size-100">
            <p v-if="loading" class="md-body-1">Loading federated users…</p>
            <template v-else>
                <p v-if="!hasProvider" class="md-body-1">
                    Configure and enable federation on the Federation tab to manage federated users.
                </p>
                <template v-else-if="!federationEnabled">
                    <p class="md-body-1">Federation is disabled. Enable it on the Federation tab to manage users here.</p>
                </template>
                <template v-else>
                    <div class="add-user-row">
                        <md-autocomplete
                            class="add-user-search"
                            autocomplete="off"
                            v-model="userSearchDisplay"
                            :md-options="userSearchOptions"
                            @md-selected="onUserSelected"
                            @md-changed="onUserSearchChanged"
                        >
                            <label>User to add</label>
                            <template slot="md-autocomplete-item" slot-scope="{ item }">
                                <span v-text="formatUserOption(item)"></span>
                            </template>
                            <template slot="md-autocomplete-empty" slot-scope="{ term }">
                                <p v-if="(term || '').length > 3">No users matching "{{ term }}" were found.</p>
                                <p v-else>Type at least 4 letters to start searching</p>
                            </template>
                        </md-autocomplete>
                        <md-button class="md-primary add-user-btn" :disabled="saving || !selectedUser" @click="addUser">
                            Add user
                        </md-button>
                    </div>

                    <div class="federation-users-table">
                        <md-table class="table-striped">
                            <md-table-row>
                                <md-table-head
                                    class="index-table-head sortable"
                                    @click.native="toggleSort('firstName')"
                                >
                                    First name
                                    <div v-if="sort.column === 'firstName' && sort.direction === 'asc'" class="icon-container">
                                        <md-icon>arrow_drop_down</md-icon>
                                    </div>
                                    <div v-if="sort.column === 'firstName' && sort.direction === 'desc'" class="icon-container">
                                        <md-icon>arrow_drop_up</md-icon>
                                    </div>
                                </md-table-head>
                                <md-table-head
                                    class="index-table-head sortable"
                                    @click.native="toggleSort('lastName')"
                                >
                                    Last name
                                    <div v-if="sort.column === 'lastName' && sort.direction === 'asc'" class="icon-container">
                                        <md-icon>arrow_drop_down</md-icon>
                                    </div>
                                    <div v-if="sort.column === 'lastName' && sort.direction === 'desc'" class="icon-container">
                                        <md-icon>arrow_drop_up</md-icon>
                                    </div>
                                </md-table-head>
                                <md-table-head
                                    class="index-table-head sortable"
                                    @click.native="toggleSort('email')"
                                >
                                    Email
                                    <div v-if="sort.column === 'email' && sort.direction === 'asc'" class="icon-container">
                                        <md-icon>arrow_drop_down</md-icon>
                                    </div>
                                    <div v-if="sort.column === 'email' && sort.direction === 'desc'" class="icon-container">
                                        <md-icon>arrow_drop_up</md-icon>
                                    </div>
                                </md-table-head>
                                <md-table-head
                                    class="index-table-head sortable text-center"
                                    @click.native="toggleSort('active')"
                                >
                                    Is active
                                    <div v-if="sort.column === 'active' && sort.direction === 'asc'" class="icon-container">
                                        <md-icon>arrow_drop_down</md-icon>
                                    </div>
                                    <div v-if="sort.column === 'active' && sort.direction === 'desc'" class="icon-container">
                                        <md-icon>arrow_drop_up</md-icon>
                                    </div>
                                </md-table-head>
                                <md-table-head
                                    class="index-table-head sortable"
                                    @click.native="toggleSort('externalId')"
                                >
                                    External ID
                                    <div v-if="sort.column === 'externalId' && sort.direction === 'asc'" class="icon-container">
                                        <md-icon>arrow_drop_down</md-icon>
                                    </div>
                                    <div v-if="sort.column === 'externalId' && sort.direction === 'desc'" class="icon-container">
                                        <md-icon>arrow_drop_up</md-icon>
                                    </div>
                                </md-table-head>
                                <md-table-head class="index-table-head federation-users-actions-head"/>
                            </md-table-row>
                            <md-table-row class="search">
                                <md-table-cell>
                                    <md-field md-inline>
                                        <md-input v-model="filters.firstName"/>
                                    </md-field>
                                </md-table-cell>
                                <md-table-cell>
                                    <md-field md-inline>
                                        <md-input v-model="filters.lastName"/>
                                    </md-field>
                                </md-table-cell>
                                <md-table-cell>
                                    <md-field md-inline>
                                        <md-input v-model="filters.email"/>
                                    </md-field>
                                </md-table-cell>
                                <md-table-cell>
                                    <md-field>
                                        <md-select v-model="filters.active" md-dense>
                                            <md-option value="">-</md-option>
                                            <md-option value="true">Yes</md-option>
                                            <md-option value="false">No</md-option>
                                        </md-select>
                                    </md-field>
                                </md-table-cell>
                                <md-table-cell>
                                    <md-field md-inline>
                                        <md-input v-model="filters.externalId" placeholder="* = has value"/>
                                    </md-field>
                                </md-table-cell>
                                <md-table-cell/>
                            </md-table-row>
                            <md-table-row v-for="u in paginatedUsers" :key="u.id">
                                <md-table-cell>{{ u.first_name }}</md-table-cell>
                                <md-table-cell>{{ u.last_name }}</md-table-cell>
                                <md-table-cell>{{ u.email }}</md-table-cell>
                                <index-table-cell-bool :bool="Boolean(u.active)"/>
                                <md-table-cell>{{ u.federated_external_id || '—' }}</md-table-cell>
                                <md-table-cell class="federation-users-actions-cell">
                                    <md-button
                                        type="button"
                                        class="md-icon-button md-dense federation-unlink-btn"
                                        title="Remove from federation"
                                        aria-label="Remove from federation"
                                        :disabled="unlinkingId === u.id || saving"
                                        @click="unlinkUser(u)"
                                    >
                                        <md-icon class="federation-unlink-icon">clear</md-icon>
                                    </md-button>
                                </md-table-cell>
                            </md-table-row>
                        </md-table>
                        <p v-if="filteredUsers.length === 0" class="md-body-1 federation-users-empty">
                            {{ users.length === 0
                                ? 'No users are linked to this federation provider yet.'
                                : 'No federated users match the current filters.' }}
                        </p>
                        <div class="federation-users-table-actions">
                            <md-field class="per-page">
                                <label>Per page</label>
                                <md-select v-model="pageLimit">
                                    <md-option v-for="option in pageLimitOptions" :key="option" :value="option">
                                        {{ option }}
                                    </md-option>
                                </md-select>
                            </md-field>
                            <pagination v-model="pageNumber" :per-page="pageLimit" :total="filteredUsers.length"/>
                        </div>
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import alerts from '../../../js/alerts';
import Pagination from '../../components/Pagination';
import IndexTableCellBool from '../../components/Index/IndexTableCellBool';

export default {
    name: 'OrganizationTabFederationUsers',
    components: {Pagination, IndexTableCellBool},
    data() {
        return {
            loading: true,
            saving: false,
            hasProvider: false,
            federationEnabled: false,
            users: [],
            userSearchDisplay: '',
            userSearchOptions: [],
            selectedUser: null,
            unlinkingId: null,
            filters: {
                firstName: '',
                lastName: '',
                email: '',
                active: '',
                externalId: '',
            },
            sort: {
                column: 'lastName',
                direction: 'asc',
            },
            pageNumber: 1,
            pageLimit: this.$cookies.get('perPage') ? Number(this.$cookies.get('perPage')) : 10,
        };
    },
    computed: {
        organizationId() {
            return this.$route.params.id;
        },
        pageLimitOptions() {
            return [10, 25, 50];
        },
        filteredUsers() {
            return this.users.filter((user) => {
                const firstName = String(user.first_name || '').toLowerCase();
                const lastName = String(user.last_name || '').toLowerCase();
                const email = String(user.email || '').toLowerCase();
                const filterFirst = this.filters.firstName.trim().toLowerCase();
                const filterLast = this.filters.lastName.trim().toLowerCase();
                const filterEmail = this.filters.email.trim().toLowerCase();

                if (filterFirst && !firstName.includes(filterFirst)) {
                    return false;
                }
                if (filterLast && !lastName.includes(filterLast)) {
                    return false;
                }
                if (filterEmail && !email.includes(filterEmail)) {
                    return false;
                }
                if (this.filters.active === 'true' && !user.active) {
                    return false;
                }
                if (this.filters.active === 'false' && user.active) {
                    return false;
                }
                if (!this.matchesExternalIdFilter(user.federated_external_id, this.filters.externalId)) {
                    return false;
                }
                return true;
            });
        },
        sortedUsers() {
            const list = [...this.filteredUsers];
            const {column, direction} = this.sort;

            list.sort((a, b) => {
                let left;
                let right;

                switch (column) {
                    case 'firstName':
                        left = String(a.first_name || '');
                        right = String(b.first_name || '');
                        break;
                    case 'lastName':
                        left = String(a.last_name || '');
                        right = String(b.last_name || '');
                        break;
                    case 'email':
                        left = String(a.email || '');
                        right = String(b.email || '');
                        break;
                    case 'active':
                        left = a.active ? 1 : 0;
                        right = b.active ? 1 : 0;
                        break;
                    case 'externalId':
                        left = String(a.federated_external_id || '');
                        right = String(b.federated_external_id || '');
                        break;
                    default:
                        return 0;
                }

                if (column === 'active') {
                    if (left < right) {
                        return direction === 'asc' ? -1 : 1;
                    }
                    if (left > right) {
                        return direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                }

                const comparison = left.localeCompare(right, undefined, {
                    sensitivity: 'base',
                    numeric: true,
                });
                return direction === 'asc' ? comparison : -comparison;
            });

            return list;
        },
        paginatedUsers() {
            const start = (this.pageNumber - 1) * this.pageLimit;
            return this.sortedUsers.slice(start, start + this.pageLimit);
        },
    },
    watch: {
        '$route.params.tab'(tab) {
            if (tab === 'federation-users') {
                this.loadUsers();
            }
        },
        filters: {
            deep: true,
            handler() {
                this.pageNumber = 1;
            },
        },
        pageLimit(value) {
            this.$cookies.set('perPage', value);
            this.pageNumber = 1;
        },
        filteredUsers() {
            const maxPage = Math.max(1, Math.ceil(this.filteredUsers.length / this.pageLimit) || 1);
            if (this.pageNumber > maxPage) {
                this.pageNumber = maxPage;
            }
        },
    },
    mounted() {
        if (this.$route.params.tab === 'federation-users') {
            this.loadUsers();
        }
    },
    methods: {
        formatUserOption(user) {
            if (!user) {
                return '';
            }
            const email = user.email ? ' - ' + user.email : '';
            return (user.first_name || '') + ' ' + (user.last_name || '') + email;
        },
        onUserSelected(user) {
            this.selectedUser = user;
            this.userSearchDisplay = this.formatUserOption(user);
        },
        onUserSearchChanged: debounce(function (searchTerm) {
            const term = searchTerm == null ? '' : String(searchTerm);
            if (this.selectedUser && this.formatUserOption(this.selectedUser) !== term) {
                this.selectedUser = null;
            }
            if (term.length > 3) {
                this.userSearchOptions = this.$api
                    .usersForHQManagers({
                        data: {search: term},
                    })
                    .then(({data}) => data || []);
            } else {
                this.selectedUser = null;
                this.userSearchOptions = Promise.resolve([]);
            }
        }, 650),
        orgSegments() {
            return {organizationId: this.organizationId};
        },
        matchesExternalIdFilter(externalId, rawFilter) {
            const filter = String(rawFilter || '').trim();
            if (filter === '') {
                return true;
            }
            if (filter === '*') {
                return externalId !== null && externalId !== undefined && String(externalId).trim() !== '';
            }
            return String(externalId || '').toLowerCase().includes(filter.toLowerCase());
        },
        toggleSort(column) {
            if (this.sort.column !== column) {
                this.sort = {column, direction: 'asc'};
                return;
            }
            this.sort = {
                column,
                direction: this.sort.direction === 'asc' ? 'desc' : 'asc',
            };
        },
        collectApiErrorMessages(err, fallbackMessage) {
            const body = err && err.response && err.response.data;
            if (!body) {
                return [fallbackMessage];
            }
            if (Array.isArray(body.messages) && body.messages.length > 0) {
                return body.messages.map((message) => message.content || message).filter(Boolean);
            }
            const nestedErrors = body.data && body.data.errors;
            const topLevelErrors = body.errors;
            const errors = nestedErrors || topLevelErrors;
            if (errors && typeof errors === 'object') {
                const messages = [];
                Object.keys(errors).forEach((key) => {
                    const fieldMessages = errors[key];
                    if (Array.isArray(fieldMessages)) {
                        fieldMessages.forEach((msg) => messages.push(msg));
                    }
                });
                if (messages.length > 0) {
                    return messages;
                }
            }
            return [body.message || err.message || fallbackMessage];
        },
        showApiError(err, fallbackMessage) {
            this.collectApiErrorMessages(err, fallbackMessage).forEach((message) => {
                alerts.error(message);
            });
        },
        unlinkUser(user) {
            if (!user || !user.id) {
                return;
            }
            this.unlinkingId = user.id;
            this.$api.organizationFederationRemoveUser({
                segments: {...this.orgSegments(), userId: user.id},
            })
                .then(() => {
                    this.loadUsers();
                    if (this.$bus) {
                        this.$bus.emit('organizationFederationChanged', {organizationId: this.organizationId});
                    }
                })
                .catch((err) => {
                    this.showApiError(err, 'Could not remove user from federation');
                })
                .finally(() => {
                    this.unlinkingId = null;
                });
        },
        loadUsers() {
            this.loading = true;
            this.$api.organizationFederationShow({segments: this.orgSegments()})
                .then(({data}) => {
                    this.hasProvider = true;
                    this.federationEnabled = data.federation_enabled !== false;
                    this.users = data.users || [];
                })
                .catch((err) => {
                    const status = err.response && err.response.status;
                    if (status === 404) {
                        this.hasProvider = false;
                        this.federationEnabled = false;
                        this.users = [];
                        return;
                    }
                    throw err;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        isUserAlreadyLinked(email) {
            const normalized = String(email || '').trim().toLowerCase();
            return this.users.some((user) => String(user.email || '').trim().toLowerCase() === normalized);
        },
        addUser() {
            if (!this.selectedUser || !this.selectedUser.email) {
                return;
            }
            if (this.isUserAlreadyLinked(this.selectedUser.email)) {
                alerts.error('This user is already linked to this federation provider.');
                return;
            }
            this.saving = true;
            this.$api.organizationFederationAddUser({
                segments: this.orgSegments(),
                data: {email: this.selectedUser.email},
            })
                .then(() => {
                    this.selectedUser = null;
                    this.userSearchDisplay = '';
                    this.userSearchOptions = [];
                    this.loadUsers();
                    if (this.$bus) {
                        this.$bus.emit('organizationFederationChanged', {organizationId: this.organizationId});
                    }
                })
                .catch((err) => {
                    this.showApiError(err, 'Could not add user to federation');
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style scoped>
.add-user-row {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
}

.add-user-search {
    flex: 0 1 320px;
    min-width: 240px;
    max-width: 400px;
}

.add-user-btn {
    margin: 0;
    flex-shrink: 0;
}

.federation-users-empty {
    margin-top: 16px;
}

.federation-users-actions-head {
    width: 56px;
}

.federation-users-actions-cell {
    text-align: right;
    vertical-align: middle;
}

.federation-unlink-btn {
    margin: 0;
}

.federation-unlink-icon {
    color: rgba(0, 0, 0, 0.54);
}
</style>

<style lang="scss">
.federation-users-tab {
    .table-striped tbody > tr:nth-of-type(odd) {
        background-color: transparent !important;
    }

    .table-striped tbody > tr:nth-of-type(even) {
        background-color: #f9f9f9;
    }

    .search .md-table-cell {
        padding: 0 12px;
        border-top: none;
    }

    .federation-users-table-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0 0;

        .per-page {
            width: auto !important;
            margin: 0;
        }
    }

    /* md-card-header forces white links; restore pagination colours like /users */
    .pagination > .page-item > .page-link {
        color: #3c4858 !important;

        &:hover,
        &:focus {
            color: #3c4858 !important;
        }

        .md-icon {
            color: #3c4858 !important;
        }
    }

    .pagination > .page-item.active > .page-link {
        &,
        &:focus,
        &:hover {
            color: #fff !important;

            .md-icon {
                color: #fff !important;
            }
        }
    }

    .index-table-head {
        user-select: none;
        background-color: #fff;

        &.sortable:hover {
            background-color: #f1f1f1;
            cursor: pointer;
        }

        .icon-container {
            float: right;
            display: flex;
            align-items: center;
            width: 24px;
            height: 100%;
        }
    }
}
</style>
